import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  LocalOffer as CouponIcon,
  StarBorder as StarIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { billingApi, Plan, SubscriptionWithUsage, type CouponValidateResponse } from '@/api/endpoints/billing';
import { formatCurrency, extractErrorMessage } from '@/utils/format';
import GatewaySelector from '@/components/GatewaySelector';
import { useAuthStore } from '@/stores/authStore';
import type { UpgradeReason } from '@/stores/upgradeModalStore';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Present when the modal is triggered by a plan-limit failure rather than
   * a proactive click. Renders an inline banner telling the user exactly
   * which cap they hit and on which plan.
   */
  reason?: UpgradeReason | null;
}

type Interval = 'monthly' | 'annual';

const HIDDEN_SLUGS = new Set(['free']); // Free isn't an upgrade target

// Per-resource pitch that gives the user a concrete reason to act. Paired
// with the structured limit details in the reason banner — the user sees
// "You're at 10/10 employees on Starter" plus "New hires are blocked on
// your current plan until you upgrade." See BRAND_VOICE.md: matter-of-fact,
// no "Oops!", no exclamation marks.
const RESOURCE_PITCHES: Record<string, string> = {
  employees: "New hires are blocked on your current plan until you upgrade.",
  companies: "Adding a new entity requires a higher plan.",
  users: "Inviting new admins requires a higher plan.",
};

function pitchFor(resource: string | undefined): string {
  if (!resource) return "Upgrade to continue.";
  return RESOURCE_PITCHES[resource] || "Upgrade to continue.";
}

/**
 * Classify a THROWN validateCoupon error (PAY-008). A genuinely invalid/expired
 * coupon returns HTTP 200 with {valid:false}, so an exception here always means
 * the request itself failed — never that the code is invalid. 429 → the tenant
 * hit the per-code rate limit; anything else → transient/unavailable.
 */
export function couponRequestError(err: unknown): 'rate_limited' | 'unavailable' {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 429 ? 'rate_limited' : 'unavailable';
}

/** Map a coupon error code to the message shown under the promo field. */
export function couponErrorMessage(error: string | undefined): string {
  switch (error) {
    case 'expired':
      return 'This code has expired.';
    case 'exhausted':
      return 'This code has reached its usage limit.';
    case 'wrong_plan':
      return 'This code does not apply to the selected plan.';
    case 'not_started':
      return 'This code is not yet valid.';
    case 'rate_limited':
      return 'Too many attempts — please wait a minute and try again.';
    case 'unavailable':
      return "Couldn't check that code right now — please try again.";
    default:
      return 'Invalid promo code. Please check and try again.';
  }
}

/**
 * In-app checkout — lists plans, toggles monthly/annual, redirects to the
 * gateway's hosted checkout. Gateway-agnostic: backend resolves the correct
 * provider from the tenant's country profile. Today only Stripe is wired
 * up (Sprint 2 adds Paystack/Flutterwave).
 */
export default function UpgradeModal({ open, onClose, reason }: UpgradeModalProps) {
  const [interval, setInterval] = useState<Interval>('monthly');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [gateway, setGateway] = useState<string | null>(null);

  // BIL-03 — promo code state
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoResult, setPromoResult] = useState<CouponValidateResponse | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [validatedCouponCode, setValidatedCouponCode] = useState<string | null>(null);
  const promoInputRef = useRef<HTMLInputElement>(null);

  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const companies = useAuthStore((s) => s.companies);
  const countryCode = useMemo(
    () =>
      companies.find((c) => c.company_id === activeCompanyId)?.country_code ?? null,
    [companies, activeCompanyId],
  );

  const { data: plans, isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ['billing', 'plans', countryCode],
    queryFn: () => billingApi.listPlans(countryCode ?? undefined).then((r) => r.data),
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });

  const { data: subscription } = useQuery<SubscriptionWithUsage>({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscription().then((r) => r.data),
    enabled: open,
    retry: false,
  });

  const checkout = useMutation({
    mutationFn: ({
      planSlug,
      intv,
      gw,
      couponCode,
    }: {
      planSlug: string;
      intv: Interval;
      gw: string | null;
      couponCode?: string;
    }) =>
      billingApi.createCheckout(planSlug, intv, gw ?? undefined, couponCode).then((r) => r.data),
    onSuccess: (data) => {
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    },
  });

  const visiblePlans = useMemo(
    () =>
      (plans ?? [])
        .filter((p) => p.is_active && !HIDDEN_SLUGS.has(p.slug))
        .sort((a, b) => a.sort_order - b.sort_order),
    [plans],
  );

  const currentSlug = subscription?.plan_slug ?? null;

  // AI-polished pitch — fires only when the modal opens with a reason (a
  // reactive plan-limit hit). Returns { pitch, is_ai }; `is_ai=false` means
  // the server fell back to the static template (no ANTHROPIC_API_KEY, quota
  // exhausted, or an error). Either way we show the pitch — it's a one-
  // sentence explainer, not a load-bearing feature.
  const { data: pitchData } = useQuery<{ pitch: string; is_ai: boolean }>({
    queryKey: [
      'billing',
      'upgrade-pitch',
      reason?.resource,
      reason?.current,
      reason?.limit,
      reason?.plan,
    ],
    queryFn: () =>
      billingApi
        .getUpgradePitch({
          resource: reason!.resource,
          current: reason!.current,
          limit: reason!.limit,
          plan: reason!.plan,
        })
        .then((r) => r.data),
    enabled: open && reason != null,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // Reactive pre-selection: when the modal opens because a tenant hit a
  // plan limit, auto-select the smallest plan that clears the offending
  // cap. Saves a click and signals which plan actually solves the problem.
  // Does nothing for proactive opens (no reason) or when the reason doesn't
  // target a resource we know how to match against plan limits.
  useEffect(() => {
    if (!open || !reason || !visiblePlans.length || selectedSlug) return;
    const neededCount = reason.current + 1;
    const capOf = (p: Plan) => {
      if (reason.resource === 'employees') return p.max_employees;
      if (reason.resource === 'companies') return p.max_companies;
      if (reason.resource === 'users') return p.included_users * 3;
      return null;
    };
    const candidate = visiblePlans.find((p) => {
      const cap = capOf(p);
      if (cap === null) return false;
      if (cap === 0) return true; // unlimited
      return cap >= neededCount && p.slug !== currentSlug;
    });
    if (candidate) setSelectedSlug(candidate.slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reason, visiblePlans.length]);

  const handleApplyPromo = async () => {
    if (!promoInput.trim() || !selectedSlug) return;
    setPromoChecking(true);
    setPromoResult(null);
    try {
      const res = await billingApi.validateCoupon({
        code: promoInput.trim().toUpperCase(),
        plan_slug: selectedSlug,
        country_code: countryCode ?? undefined,
      });
      setPromoResult(res.data);
      if (res.data.valid) {
        setValidatedCouponCode(promoInput.trim().toUpperCase());
      } else {
        setValidatedCouponCode(null);
      }
    } catch (err) {
      // Reaching this catch means the REQUEST failed (network / 429 / 5xx) — a
      // genuinely invalid coupon returns 200 with {valid:false} above. Surface
      // that honestly instead of blaming the code (PAY-008).
      setPromoResult({ valid: false, error: couponRequestError(err) });
      setValidatedCouponCode(null);
    } finally {
      setPromoChecking(false);
    }
  };

  const handleUpgrade = () => {
    if (!selectedSlug) return;
    import('@/lib/analytics').then(({ analytics }) => {
      analytics.upgradeClicked({ source: 'upgrade_modal', plan_slug: selectedSlug });
      analytics.checkoutStarted({
        plan_slug: selectedSlug,
        gateway: gateway ?? 'default',
        interval,
      });
    });
    checkout.mutate({ planSlug: selectedSlug, intv: interval, gw: gateway, couponCode: validatedCouponCode ?? undefined });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <StarIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold }}>
            Upgrade your plan
          </Typography>
          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
            Pick a plan and we'll send you to secure checkout.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close upgrade dialog"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {reason && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold }}>
              You're at {reason.current}/{reason.limit} {reason.resource} on the {reason.plan} plan.
            </Typography>
            <Typography sx={{ fontSize: TYPE.xs, mt: 0.5, color: 'text.secondary' }}>
              {pitchData?.pitch ?? pitchFor(reason.resource)}
            </Typography>
          </Alert>
        )}
        <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={interval}
            exclusive
            size="small"
            onChange={(_, v) => v && setInterval(v)}
            aria-label="Billing interval"
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="annual">Annual · 2 months free</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {plansLoading ? (
          <Stack alignItems="center" py={6}>
            <CircularProgress size={28} />
          </Stack>
        ) : visiblePlans.length === 0 ? (
          <Alert severity="info">No upgrade plans are available right now.</Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${Math.min(visiblePlans.length, 4)}, 1fr)` },
              gap: 2,
            }}
          >
            {visiblePlans.map((plan) => {
              const price = interval === 'monthly' ? plan.base_price_monthly : plan.base_price_annual;
              const isCurrent = plan.slug === currentSlug;
              const isSelected = plan.slug === selectedSlug;
              const isCustom = Number(price) === 0 && plan.slug === 'enterprise';

              return (
                <Box
                  key={plan.id}
                  role="button"
                  tabIndex={isCurrent ? -1 : 0}
                  aria-label={`Select ${plan.name} plan`}
                  onClick={() => !isCurrent && setSelectedSlug(plan.slug)}
                  onKeyDown={(e) => {
                    if (!isCurrent && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setSelectedSlug(plan.slug);
                    }
                  }}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isCurrent ? 'action.hover' : 'background.paper',
                    cursor: isCurrent ? 'default' : 'pointer',
                    opacity: isCurrent ? 0.6 : 1,
                    transition: 'border-color 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    '&:hover': {
                      borderColor: isCurrent ? 'divider' : 'primary.main',
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: TYPE.base, fontWeight: TYPE.semibold }}>
                      {plan.name}
                    </Typography>
                    {isCurrent && <Chip label="Current" size="small" />}
                  </Stack>
                  {plan.description && (
                    <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                      {plan.description}
                    </Typography>
                  )}
                  <Box>
                    {isCustom ? (
                      <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold }}>
                        Contact us
                      </Typography>
                    ) : (
                      <>
                        <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold }}>
                          {formatCurrency(price, plan.currency)}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                          per {interval === 'monthly' ? 'month' : 'year'}
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CheckIcon fontSize="small" color="primary" />
                      <Typography sx={{ fontSize: TYPE.xs }}>
                        {plan.included_users === 0 ? 'Unlimited users' : `${plan.included_users} users included`}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CheckIcon fontSize="small" color="primary" />
                      <Typography sx={{ fontSize: TYPE.xs }}>
                        {plan.max_companies === 0 ? 'Unlimited companies' : `Up to ${plan.max_companies} ${plan.max_companies === 1 ? 'company' : 'companies'}`}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CheckIcon fontSize="small" color="primary" />
                      <Typography sx={{ fontSize: TYPE.xs }}>
                        {plan.max_employees === 0 ? 'Unlimited employees' : `${plan.max_employees} employees`}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}

        {/* BIL-03 — Promo code section */}
        {selectedSlug && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              size="small"
              startIcon={<CouponIcon fontSize="small" />}
              onClick={() => setPromoOpen((v) => !v)}
              sx={{ color: 'text.secondary', fontSize: TYPE.xs, px: 0 }}
            >
              Have a promo code? {promoOpen ? '▲' : '▼'}
            </Button>
            <Collapse in={promoOpen}>
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                <TextField
                  inputRef={promoInputRef}
                  size="small"
                  placeholder="Enter code"
                  value={promoInput}
                  onChange={(e) => {
                    setPromoInput(e.target.value.toUpperCase());
                    if (promoResult) { setPromoResult(null); setValidatedCouponCode(null); }
                  }}
                  inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
                  sx={{ flex: 1 }}
                  InputProps={promoResult?.valid ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CheckIcon fontSize="small" color="success" />
                      </InputAdornment>
                    ),
                  } : undefined}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleApplyPromo}
                  disabled={promoChecking || !promoInput.trim()}
                >
                  {promoChecking ? <CircularProgress size={14} /> : 'Apply'}
                </Button>
              </Stack>
              {promoResult?.valid && promoResult.display_label && (
                <Chip
                  icon={<CheckIcon />}
                  label={`${promoResult.display_label} applied`}
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
              {promoResult && !promoResult.valid && (
                <Typography sx={{ fontSize: TYPE.xs, color: 'error.main', mt: 0.5 }}>
                  {couponErrorMessage(promoResult.error)}
                </Typography>
              )}
            </Collapse>
          </Box>
        )}

        {selectedSlug && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <GatewaySelector
              countryCode={countryCode}
              value={gateway}
              onChange={setGateway}
              forRecurring
            />
          </Box>
        )}

        {checkout.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {extractErrorMessage(checkout.error, "Couldn't start checkout. Please try again or contact support.")}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedSlug || selectedSlug === currentSlug || checkout.isPending}
          onClick={handleUpgrade}
          startIcon={checkout.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {checkout.isPending ? 'Redirecting…' : 'Continue to checkout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
